import type { Landmark } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import { beamSolid, frustumSolid, pyramidOverRing, ringPoints, subFrame } from '../solids';
import { rectWall } from './masonry';
import { planFrameOf, toLayout } from './planFrame';
import { dormerSolid } from './gothic/dormer';
import type { EntranceStyles } from './nieuweBrugBuildings';
import { NIEUWE_BRUG_WEST_TOWER } from '../../../site/nieuweBrugWestHead';
export { NIEUWE_BRUG_WEST_TOWER } from '../../../site/nieuweBrugWestHead';

/** Museum scan 4380,3150–4670,3450: a round shaft, pointed faceted cap and small dormer.
 * The earlier rectangular body misread its front-facing painted windows as a flat facade.
 * Width, height, foundation batter and unseen openings remain reconstruction estimates. */
export function nieuweBrugWestTowerFootprint(landmark: Landmark): Ring {
  const plan = planFrameOf(landmark.footprint, landmark.heading);
  const t = NIEUWE_BRUG_WEST_TOWER;
  return ringPoints([0, 0], t.footRadius, t.segments, 0).map((point) => toLayout(plan, point));
}

function shaft(builder: MeshBuilder, styles: EntranceStyles, frame: Frame, elevation: number): void {
  const t = NIEUWE_BRUG_WEST_TOWER;
  const masonry = { ...styles.masonry, glazing: { reveal: styles.masonry.wall } };
  const ring = ringPoints([0, 0], t.radius, t.segments, 0);
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    const centre = Math.hypot(b[0] - a[0], b[1] - a[1]) / 2;
    const opening = (sill: number, width = 0.65, height = 1.2): Ring => [
      [centre - width / 2, sill],
      [centre + width / 2, sill],
      [centre + width / 2, sill + height],
      [centre - width / 2, sill + height],
    ];
    const windows = i % 2 === 1 ? [3, 7.3].map((sill) => opening(sill)) : [];
    if (i === 11) windows.push(opening(-0.17, 1.1, 2.2));
    rectWall(builder, masonry, frame, [a, b], [-0.2, t.eaves], windows, 0);
  });
  frustumSolid(
    builder,
    masonry.wall,
    frame,
    { center: [0, 0], radius: t.footRadius, segments: t.segments, phase: 0 },
    -2.5 - elevation,
    -0.2,
    t.radius,
    { top: false, bottom: true }
  );
}

export function buildNieuweBrugWestTower(
  builder: MeshBuilder,
  styles: EntranceStyles,
  frame: Frame,
  elevation = 0
): void {
  const t = NIEUWE_BRUG_WEST_TOWER;
  const masonry = { ...styles.masonry, glazing: { reveal: styles.masonry.wall } };
  shaft(builder, styles, frame, elevation);
  frustumSolid(
    builder,
    masonry.wall,
    frame,
    { center: [0, 0], radius: t.radius, segments: t.segments, phase: 0 },
    t.eaves,
    t.eaves + 0.1,
    t.radius + 0.12,
    { top: false, bottom: false }
  );
  const roof = ringPoints([0, 0], t.radius + 0.12, t.segments, 0);
  pyramidOverRing(builder, masonry, frame, roof, t.eaves + 0.1, { plan: [0, 0], y: t.apex });
  dormerSolid(builder, masonry, subFrame(frame, 0, 0, Math.PI), {
    u: 0,
    v: -2.75,
    width: 1.4,
    depth: 1.7,
    y0: 12.2,
    eaveY: 14.6,
    ridgeY: 15.4,
  });
  beamSolid(builder, styles.timber, frame, [0, t.apex, 0], [0, 19.1, 0], 0.04);
}
